package com.downdogapp.client.controllers

import com.downdogapp.client.*
import com.downdogapp.client.api.AppType.*
import com.downdogapp.client.api.HistoryDetailsRequest
import com.downdogapp.client.api.HistoryDetailsResponse
import com.downdogapp.client.api.HistoryItem
import com.downdogapp.client.controllers.start.*
import com.downdogapp.client.singleton.*
import com.downdogapp.client.singleton.HistoryUtil.name
import com.downdogapp.client.views.HistoryItemView

class HistoryItemViewController(val item: HistoryItem) : ViewController() {

  override val view = HistoryItemView(this)

  private val openInOtherAppLink
    get() =
      if (item.appType != App.appType)
        MANIFEST.historyDeepLinks.find { it.appType == item.appType }?.link?.urls?.firstOrNull()?.let {
          "$it?practiceId=${item.practiceId}"
        }
      else
        null

  val title get() = item.name
  val subtitle get() = item.date

  val displayOpenInOtherApp get() = openInOtherAppLink != null
  val showPoseList get() = MANIFEST.showPosesOnTimeline && item.displayPoses
  val showOfflineDownload
    get() = MANIFEST.displayOfflinePracticesPage && item.displayStart
  val savedForOffline
    get() = OfflinePractices.isDownloading(item.sequenceId) || OfflinePractices.isDownloaded(item.sequenceId)

  val poseListTitle
    get() = when (item.appType) {
      ORIGINAL, INTRO, PRENATAL -> Strings.POSE_LIST
      HIIT, BARRE, SEVEN, PILATES -> Strings.EXERCISE_LIST
      MEDITATION, RUNNING, BREATHING -> ""
    }

  private var historyDetailsResponse: HistoryDetailsResponse? = null
  private var historyDetailsCallback: (() -> Unit)? = null

  init {
    if (item.displaySongs || item.displayPoses) {
      Network.post(
        HistoryDetailsRequest(item.practiceId)
      ) { response ->
        if (response == null) {
          if (historyDetailsCallback == null) {
            historyDetailsCallback = {
              App.alertNoInternet()
            }
          } else {
            App.alertNoInternet()
            view.hideSpinner()
          }
        } else {
          historyDetailsResponse = HistoryDetailsResponse.fromJson(response)
          historyDetailsCallback?.invoke()
        }
      }
    }
  }

  fun onStartPracticeClicked() {
    NewPracticePage.startPractice(item.sequenceId)
  }

  fun playlistClicked() {
    handleHistoryResponse(::openPlaylist)
  }

  fun poseListClicked() {
    handleHistoryResponse(::openPoseList)
  }

  fun openInOtherAppClicked() {
    App.openUrl(openInOtherAppLink!!)
  }

  fun downloadClicked() {
    if (savedForOffline) {
      // If already saved, go to Offline page
      StartViewController.goToPage(OfflinePracticesPage)
      App.popViewController()
    } else {
      // Otherwise we download the practice
      NewPracticePage.validateCanStartPractice {
        OfflinePractices.validateCanDownloadPractice {
          view.showSpinner()
          OfflinePractices.downloadOfflinePractice(item.sequenceId) {
            view.hideSpinner()
            view.refreshView()
          }
        }
      }
    }
  }

  fun shareClicked() {
    App.shareSequence(item.sequenceId)
  }

  fun removeFromHistoryClicked() {
    App.alert(
      title = Strings.REMOVE_FROM_HISTORY,
      message = Strings.REMOVE_FROM_HISTORY_MESSAGE,
      negativeButton = AlertAction(Strings.NO_STRING),
      positiveButton = AlertAction(Strings.YES_STRING) {
        logEvent("item_deleted", mapOf("practiceId" to item.practiceId))
        HistoryUtil.deletePractice(item.practiceId)
        HistoryPage.refreshPage()

        App.popViewController()
      }
    )
  }

  fun removeFromFavoritesClicked() =
    updateSequenceFavorite(false)

  fun addToFavoritesClicked() =
    updateSequenceFavorite(true)

  private fun handleHistoryResponse(onSuccess: () -> Unit) {
    when {
      historyDetailsResponse != null -> onSuccess()

      historyDetailsCallback == null -> {
        view.showSpinner()
        historyDetailsCallback = {
          view.hideSpinner()
          onSuccess()
        }
      }

      else -> historyDetailsCallback!!()
    }
  }

  private fun openPlaylist() = App.pushViewController(
    PlaylistViewController(historyDetailsResponse!!.songs)
  )

  private fun openPoseList() = App.pushViewController(
    PoseListViewController(
      title = poseListTitle,
      unfilteredItems = historyDetailsResponse!!.poses
    )
  )

  private fun updateSequenceFavorite(favorite: Boolean) {
    logEvent(
      "update_sequence_favorite",
      mapOf("sequenceId" to item.sequenceId, "favorite" to favorite.toString())
    )
    HistoryUtil.updateSequenceFavorite(item.sequenceId, favorite)
    HistoryPage.refreshPage() // refresh synchronously because we store the update in user prefs immediately
    view.refreshView()
  }

  fun editNameClicked() {
    logEvent(
      "edit_sequence_name",
      mapOf("sequenceId" to item.sequenceId)
    )
    App.pushViewController(
      TextInputViewController(
        title = Strings.EDIT_NAME,
        inputLabel = Strings.SUBMIT,
        initialValue = item.name,
        submitCallback = ::finishedEditingName
      )
    )
  }

  private fun finishedEditingName(name: String) {
    if (name.isNotBlank()) {
      HistoryUtil.renameSequence(item.sequenceId, name)
      HistoryPage.refreshPage() // refresh synchronously because we store the update in user prefs immediately
      view.refreshView()
    }
  }
}
